import { useEffect, useState } from "react";
import BaseLayout from "../layouts/BaseLayout";
import { apiRequest, VojomoLoader } from "@vojomo-core/shared";
import { marked } from "marked";
import {
  Button,
  Input,
  Select,
  Checkbox,
  Modal,
  Tag,
  Tooltip,
  Form,
  Divider,
} from "antd";
import {
  PlusCircle,
  Trash2,
  PencilLine,
  ChevronDown,
  ChevronRight,
  Target,
  Calendar,
  Clock,
  Wrench,
  GitMerge,
  Rocket,
  UserCheck,
} from "lucide-react";

const { TextArea } = Input;

const EXECUTIVES = ["Matt Balick", "Ross Shanken", "Rob Rokoff"];
const DEADLINE = new Date("2026-04-13");

// Seed colors for known categories; unknown categories get a deterministic color from the palette
const CATEGORY_COLORS: Record<string, string> = {
  Scoring: "#8b5cf6",
  Objectives: "#2563eb",
  UX: "#ec4899",
  Communications: "#f59e0b",
  "QBR Structural": "#0891b2",
  "A.A.R.": "#dc2626",
  "In-Session Review": "#059669",
  "Artifacts & Synthesis": "#d97706",
  "In-Session Workspace": "#7c3aed",
  General: "#64748b",
};

const COLOR_PALETTE = [
  "#8b5cf6", "#2563eb", "#ec4899", "#f59e0b", "#0891b2",
  "#dc2626", "#059669", "#d97706", "#7c3aed", "#64748b",
  "#0d9488", "#b45309", "#be185d", "#1d4ed8", "#15803d",
];

function getCategoryColor(cat: string): string {
  if (CATEGORY_COLORS[cat]) return CATEGORY_COLORS[cat];
  let hash = 0;
  for (let i = 0; i < cat.length; i++) hash = cat.charCodeAt(i) + ((hash << 5) - hash);
  return COLOR_PALETTE[Math.abs(hash) % COLOR_PALETTE.length];
}

// Final stage — treated as "done" throughout the dashboard
const FINAL_STATUS = "merged_into_production";

const STATUS_CONFIG = {
  not_started: {
    label: "Not Started",
    color: "#94a3b8",
    bg: "#f1f5f9",
    icon: <Clock size={13} />,
  },
  prototype_created: {
    label: "Prototype Created",
    color: "#f59e0b",
    bg: "#fffbeb",
    icon: <Wrench size={13} />,
  },
  merged_into_new_product: {
    label: "In New Product",
    color: "#2563eb",
    bg: "#eff6ff",
    icon: <GitMerge size={13} />,
  },
  merged_into_production: {
    label: "In Production",
    color: "#10b981",
    bg: "#ecfdf5",
    icon: <Rocket size={13} />,
  },
};

const EXEC_COLORS: Record<string, string> = {
  "Matt Balick": "#2563eb",
  "Ross Shanken": "#7c3aed",
  "Rob Rokoff": "#0891b2",
};

interface QBRSubtask {
  id: string;
  title: string;
  accountable: string | null;
  status: string;
  approved_by: string[];
  notes: string;
}

interface QBRObjective {
  id: string;
  title: string;
  description: string;
  notes: string;
  accountable: string | null;
  category: string;
  status: string;
  approved_by: string[];
  subtasks: QBRSubtask[];
  order: number;
  due_date: string;
  task_type?: string;
  timeline?: string;
  next_action?: string;
  platform_functionality_needed?: string;
  claude_task?: boolean;
}

function formatDueDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function dueDateColor(dateStr: string): string {
  if (!dateStr) return "var(--color-muted-foreground)";
  const d = new Date(dateStr + "T12:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = (d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  if (diff < 0) return "#ef4444";
  if (diff <= 3) return "#f59e0b";
  return "var(--color-muted-foreground)";
}

function daysUntilDeadline(): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = DEADLINE.getTime() - today.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}


function MarkdownText({ text, style }: { text: string; style?: React.CSSProperties }) {
  if (!text) return null;
  // Call marked() directly — same pattern used in ChatMessages.tsx
  const html = marked(text) as string;
  return (
    <div
      className="qbr-md"
      style={style}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 11);
}

export function QBRDashboard(): React.ReactElement {
  const [objectives, setObjectives] = useState<QBRObjective[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterAccountable, setFilterAccountable] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  // Extra categories created during this session (not yet in the DB)
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [newCategoryInput, setNewCategoryInput] = useState("");
  // Objective modal state
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  const [editingObjective, setEditingObjective] = useState<QBRObjective | null>(null);
  const [objectiveForm] = Form.useForm();

  // Subtask modal state
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [subtaskParentId, setSubtaskParentId] = useState<string | null>(null);
  const [editingSubtask, setEditingSubtask] = useState<QBRSubtask | null>(null);
  const [subtaskForm] = Form.useForm();

  const daysLeft = daysUntilDeadline();

  useEffect(() => {
    fetchObjectives();
  }, []);

  async function fetchObjectives() {
    try {
      setLoading(true);
      const res = await apiRequest<{ objectives: QBRObjective[] }>({
        subRoute: "admin",
        path: "/qbr/objectives",
        method: "GET",
      });
      setObjectives(res.objectives);
    } catch (err) {
      setError("Failed to load objectives.");
    } finally {
      setLoading(false);
    }
  }

  // ── Objective CRUD ──────────────────────────────────────────────────

  function openAddObjective() {
    setEditingObjective(null);
    objectiveForm.resetFields();
    setShowObjectiveModal(true);
  }

  function openEditObjective(obj: QBRObjective) {
    setEditingObjective(obj);
    objectiveForm.setFieldsValue({
      title: obj.title,
      description: obj.description,
      notes: obj.notes,
      accountable: obj.accountable,
      category: obj.category,
      status: obj.status,
      task_type: obj.task_type,
      timeline: obj.timeline,
      due_date: obj.due_date,
      next_action: obj.next_action,
      platform_functionality_needed: obj.platform_functionality_needed,
      claude_task: obj.claude_task ?? false,
    });
    setShowObjectiveModal(true);
  }

  async function saveObjective() {
    const values = await objectiveForm.validateFields();
    setSaving("objective");
    try {
      if (editingObjective) {
        const res = await apiRequest<{ objective: QBRObjective }>({
          subRoute: "admin",
          path: `/qbr/objectives/${editingObjective.id}`,
          method: "PUT",
          body: values,
        });
        setObjectives((prev) =>
          prev.map((o) => (o.id === editingObjective.id ? res.objective : o))
        );
      } else {
        const res = await apiRequest<{ objective: QBRObjective }>({
          subRoute: "admin",
          path: "/qbr/objectives",
          method: "POST",
          body: { ...values, order: objectives.length },
        });
        setObjectives((prev) => [...prev, res.objective]);
      }
      setShowObjectiveModal(false);
    } finally {
      setSaving(null);
    }
  }

  async function deleteObjective(id: string) {
    Modal.confirm({
      title: "Delete this objective?",
      content: "This will also remove all its subtasks. This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await apiRequest({ subRoute: "admin", path: `/qbr/objectives/${id}`, method: "DELETE" });
        setObjectives((prev) => prev.filter((o) => o.id !== id));
      },
    });
  }

  async function changeObjectiveAccountable(objectiveId: string, accountable: string | null) {
    await patchObjective(objectiveId, { accountable: accountable ?? undefined });
  }

  async function setObjectiveStatus(objectiveId: string, status: string) {
    await patchObjective(objectiveId, { status });
  }

  async function patchObjective(id: string, patch: Partial<QBRObjective>) {
    setSaving(id);
    try {
      const res = await apiRequest<{ objective: QBRObjective }>({
        subRoute: "admin",
        path: `/qbr/objectives/${id}`,
        method: "PUT",
        body: patch,
      });
      setObjectives((prev) => prev.map((o) => (o.id === id ? res.objective : o)));
    } finally {
      setSaving(null);
    }
  }

  // ── Subtask CRUD ────────────────────────────────────────────────────

  function openAddSubtask(parentId: string) {
    setSubtaskParentId(parentId);
    setEditingSubtask(null);
    subtaskForm.resetFields();
    setShowSubtaskModal(true);
  }

  function openEditSubtask(parentId: string, subtask: QBRSubtask) {
    setSubtaskParentId(parentId);
    setEditingSubtask(subtask);
    subtaskForm.setFieldsValue({
      title: subtask.title,
      accountable: subtask.accountable,
      status: subtask.status,
      notes: subtask.notes,
    });
    setShowSubtaskModal(true);
  }

  async function saveSubtask() {
    if (!subtaskParentId) return;
    const values = await subtaskForm.validateFields();
    const parent = objectives.find((o) => o.id === subtaskParentId);
    if (!parent) return;
    setSaving("subtask");
    try {
      let updatedSubtasks: QBRSubtask[];
      if (editingSubtask) {
        updatedSubtasks = parent.subtasks.map((s) =>
          s.id === editingSubtask.id ? { ...s, ...values } : s
        );
      } else {
        const newSub: QBRSubtask = {
          id: generateId(),
          title: values.title,
          accountable: values.accountable ?? null,
          status: values.status ?? "not_started",
          approved_by: [],
          notes: values.notes ?? "",
        };
        updatedSubtasks = [...parent.subtasks, newSub];
      }
      await patchObjective(subtaskParentId, { subtasks: updatedSubtasks as any });
      setShowSubtaskModal(false);
    } finally {
      setSaving(null);
    }
  }

  async function deleteSubtask(parentId: string, subtaskId: string) {
    const parent = objectives.find((o) => o.id === parentId);
    if (!parent) return;
    const updatedSubtasks = parent.subtasks.filter((s) => s.id !== subtaskId);
    await patchObjective(parentId, { subtasks: updatedSubtasks as any });
  }

  async function setSubtaskStatus(parentId: string, subtaskId: string, status: string) {
    const parent = objectives.find((o) => o.id === parentId);
    if (!parent) return;
    const updatedSubtasks = parent.subtasks.map((s) =>
      s.id === subtaskId ? { ...s, status } : s
    );
    await patchObjective(parentId, { subtasks: updatedSubtasks as any });
  }

  async function changeSubtaskAccountable(parentId: string, subtaskId: string, accountable: string | null) {
    const parent = objectives.find((o) => o.id === parentId);
    if (!parent) return;
    const updatedSubtasks = parent.subtasks.map((s) =>
      s.id === subtaskId ? { ...s, accountable } : s
    );
    await patchObjective(parentId, { subtasks: updatedSubtasks as any });
  }

  // ── Computed stats ──────────────────────────────────────────────────

  const totalObjectives = objectives.length;
  const inProduction = objectives.filter((o) => o.status === FINAL_STATUS).length;
  const inNewProduct = objectives.filter((o) => o.status === "merged_into_new_product").length;
  const prototypeCreated = objectives.filter((o) => o.status === "prototype_created").length;

  const totalSubtasks = objectives.reduce((acc, o) => acc + o.subtasks.length, 0);
  const doneSubtasks = objectives.reduce(
    (acc, o) => acc + o.subtasks.filter((s) => s.status === FINAL_STATUS).length,
    0
  );
  // Category breakdown
  const uniqueCategories = Array.from(new Set(objectives.map((o) => o.category))).sort();
  const allCategories = Array.from(new Set([...uniqueCategories, ...customCategories])).sort();
  const categoryStats = uniqueCategories.map((cat) => {
    const objs = objectives.filter((o) => o.category === cat);
    return {
      cat,
      total: objs.length,
      prototype: objs.filter((o) => o.status === "prototype_created").length,
      inNewProduct: objs.filter((o) => o.status === "merged_into_new_product").length,
      done: objs.filter((o) => o.status === FINAL_STATUS).length,
    };
  });

  // Filtered list
  const filteredObjectives = objectives.filter((o) => {
    if (filterCategory && o.category !== filterCategory) return false;
    if (filterStatus && o.status !== filterStatus) return false;
    if (filterAccountable && o.accountable !== filterAccountable) return false;
    return true;
  });

  const activeFilterCount = [filterCategory, filterStatus, filterAccountable].filter(Boolean).length;

  if (loading) {
    return (
      <BaseLayout pageTitle="CSG QBR Dashboard">
        <div className="flex items-center justify-center h-[80vh]">
          <VojomoLoader />
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout pageTitle="CSG QBR Dashboard">
        <div className="flex items-center justify-center h-[80vh]">
          <p className="text-red-500">{error}</p>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout pageTitle="CSG QBR Dashboard" contactFooter={false}>
      {/* ── Outer flex column: banner fixed, body scrolls ──────────── */}
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          background: "linear-gradient(135deg, var(--color-indigo-900) 0%, var(--color-indigo-800) 100%)",
          borderRadius: "var(--radius)",
          padding: "24px 32px",
          marginBottom: 24,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <Target size={20} strokeWidth={2} />
            <span
              style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", opacity: 0.75, textTransform: "uppercase" }}
            >
              CSG Quarterly Business Review
            </span>
          </div>
          <h3
            style={{
              fontFamily: "'Albert Sans', sans-serif",
              fontSize: 26,
              fontWeight: 600,
              color: "#fff",
              margin: 0,
              padding: 0,
              height: "auto",
            }}
          >
            Platform Readiness Dashboard
          </h3>
          <p style={{ margin: "6px 0 0", opacity: 0.7, fontSize: 13 }}>
            Track objectives, assign accountability, and get executive sign-off before April 13
          </p>
        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "flex-end",
              marginBottom: 8,
            }}
          >
            <Calendar size={16} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>April 13, 2026</span>
          </div>
          <div
            style={{
              background: daysLeft <= 7 ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.12)",
              border: `1px solid ${daysLeft <= 7 ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.2)"}`,
              borderRadius: 10,
              padding: "8px 18px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{daysLeft}</div>
            <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>days remaining</div>
          </div>
        </div>
      </div>

      {/* ── Scrollable body — takes all remaining space ────────────── */}
      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 32 }}>

      {/* ── Stats Row ──────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 14,
          marginBottom: 24,
        }}
      >
        {[
          { label: "Total Objectives", value: totalObjectives, color: "#2563eb" },
          { label: "Prototype Created", value: prototypeCreated, color: "#f59e0b" },
          { label: "In New Product", value: inNewProduct, color: "#2563eb" },
          { label: "In Production", value: inProduction, color: "#10b981" },
          { label: "Subtasks Done", value: `${doneSubtasks}/${totalSubtasks}`, color: "#06b6d4" },
        ].map((s) => (
          <div
            key={s.label}
            className="panel"
            style={{ padding: "16px 20px" }}
          >
            <div style={{ fontSize: 11, color: "var(--color-muted-foreground)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* ── Overall Progress Bar ───────────────────────────────────── */}
      <div className="panel" style={{ padding: "16px 20px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-foreground)" }}>
            Overall Pipeline
          </span>
          <span style={{ fontSize: 13, color: "var(--color-muted-foreground)" }}>
            {totalObjectives} objectives
          </span>
        </div>
        {/* Segmented pipeline bar */}
        <div style={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden", background: "var(--color-muted)", gap: 1 }}>
          {inProduction > 0 && (
            <div style={{ width: `${Math.round((inProduction / totalObjectives) * 100)}%`, background: STATUS_CONFIG.merged_into_production.color, transition: "width 0.4s" }} />
          )}
          {inNewProduct > 0 && (
            <div style={{ width: `${Math.round((inNewProduct / totalObjectives) * 100)}%`, background: STATUS_CONFIG.merged_into_new_product.color, transition: "width 0.4s" }} />
          )}
          {prototypeCreated > 0 && (
            <div style={{ width: `${Math.round((prototypeCreated / totalObjectives) * 100)}%`, background: STATUS_CONFIG.prototype_created.color, transition: "width 0.4s" }} />
          )}
        </div>
        {/* Legend */}
        <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
          {(
            [
              { key: "merged_into_production", label: "In Production", count: inProduction },
              { key: "merged_into_new_product", label: "In New Product", count: inNewProduct },
              { key: "prototype_created", label: "Prototype", count: prototypeCreated },
              { key: "not_started", label: "Not Started", count: totalObjectives - inProduction - inNewProduct - prototypeCreated },
            ] as const
          ).map(({ key, label, count }) => (
            <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--color-muted-foreground)" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_CONFIG[key].color, display: "inline-block", flexShrink: 0 }} />
              <span style={{ fontWeight: 600, color: STATUS_CONFIG[key].color }}>{count}</span>
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Progress by Category ───────────────────────────────────── */}
      {categoryStats.length > 0 && (
        <div className="panel" style={{ padding: "16px 20px", marginBottom: 24 }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-foreground)" }}>
              Progress by Category
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 10,
            }}
          >
            {categoryStats.map(({ cat, total, prototype: catProto, inNewProduct: catNew, done: catDone }) => {
              const color = getCategoryColor(cat);
              const isActive = filterCategory === cat;
              const notStarted = total - catProto - catNew - catDone;
              // Segment widths as percentages
              const seg = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));
              return (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(isActive ? null : cat)}
                  style={{
                    background: isActive ? `${color}12` : "var(--color-muted)",
                    border: `1.5px solid ${isActive ? color : "transparent"}`,
                    borderRadius: 10,
                    padding: "10px 14px",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {/* Category name + in-prod count */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? color : "var(--color-foreground)", letterSpacing: "0.04em" }}>
                      {cat}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--color-muted-foreground)" }}>
                      {catDone}/{total}
                    </span>
                  </div>

                  {/* Segmented pipeline bar */}
                  <div style={{ display: "flex", height: 6, borderRadius: 999, overflow: "hidden", gap: 1, background: "var(--color-border)" }}>
                    {catDone > 0 && (
                      <div style={{ width: `${seg(catDone)}%`, background: STATUS_CONFIG.merged_into_production.color, transition: "width 0.4s" }} />
                    )}
                    {catNew > 0 && (
                      <div style={{ width: `${seg(catNew)}%`, background: STATUS_CONFIG.merged_into_new_product.color, transition: "width 0.4s" }} />
                    )}
                    {catProto > 0 && (
                      <div style={{ width: `${seg(catProto)}%`, background: STATUS_CONFIG.prototype_created.color, transition: "width 0.4s" }} />
                    )}
                    {notStarted > 0 && (
                      <div style={{ flex: 1, background: "transparent" }} />
                    )}
                  </div>

                  {/* Stage counts — horizontal, compact */}
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {catDone > 0 && (
                      <span style={{ fontSize: 10, color: STATUS_CONFIG.merged_into_production.color, fontWeight: 600 }}>
                        {catDone} prod
                      </span>
                    )}
                    {catNew > 0 && (
                      <span style={{ fontSize: 10, color: STATUS_CONFIG.merged_into_new_product.color, fontWeight: 600 }}>
                        {catNew} new
                      </span>
                    )}
                    {catProto > 0 && (
                      <span style={{ fontSize: 10, color: STATUS_CONFIG.prototype_created.color, fontWeight: 600 }}>
                        {catProto} proto
                      </span>
                    )}
                    {notStarted > 0 && (
                      <span style={{ fontSize: 10, color: "var(--color-muted-foreground)", fontWeight: 600 }}>
                        {notStarted} not started
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Objectives List ────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--color-foreground)" }}>
          Objectives
          {filteredObjectives.length !== objectives.length && (
            <span style={{ fontSize: 13, fontWeight: 400, color: "var(--color-muted-foreground)", marginLeft: 8 }}>
              {filteredObjectives.length} of {objectives.length}
            </span>
          )}
        </h4>
        <Button
          type="primary"
          icon={<PlusCircle size={15} />}
          onClick={openAddObjective}
          style={{
            background: "var(--color-indigo-900)",
            border: "none",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Add Objective
        </Button>
      </div>

      {/* ── Filter drawers ─────────────────────────────────────────── */}
      <FilterDrawers
        uniqueCategories={uniqueCategories}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        filterAccountable={filterAccountable}
        setFilterAccountable={setFilterAccountable}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        activeFilterCount={activeFilterCount}
        onClearAll={() => { setFilterCategory(null); setFilterAccountable(null); setFilterStatus(null); }}
      />

      {filteredObjectives.length === 0 && objectives.length > 0 && (
        <div
          className="panel"
          style={{ padding: 32, textAlign: "center", color: "var(--color-muted-foreground)", marginBottom: 12 }}
        >
          <p style={{ fontSize: 14, margin: 0 }}>No objectives match this filter.</p>
          <button
            onClick={() => { setFilterCategory(null); setFilterAccountable(null); setFilterStatus(null); }}
            style={{ marginTop: 8, fontSize: 12, color: "var(--color-primary)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
          >
            Clear filters
          </button>
        </div>
      )}

      {objectives.length === 0 && (
        <div
          className="panel"
          style={{
            padding: 48,
            textAlign: "center",
            color: "var(--color-muted-foreground)",
          }}
        >
          <Target size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ fontSize: 15, margin: 0 }}>No objectives yet.</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>Click "Add Objective" to get started.</p>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filteredObjectives.map((obj) => (
          <ObjectiveCard
            key={obj.id}
            objective={obj}
            saving={saving}
            onEdit={() => openEditObjective(obj)}
            onDelete={() => deleteObjective(obj.id)}
            onAccountableChange={(a) => changeObjectiveAccountable(obj.id, a)}
            onStatusChange={(status) => setObjectiveStatus(obj.id, status)}
            onAddSubtask={() => openAddSubtask(obj.id)}
            onEditSubtask={(s) => openEditSubtask(obj.id, s)}
            onDeleteSubtask={(sid) => deleteSubtask(obj.id, sid)}
            onSubtaskStatusChange={(sid, status) => setSubtaskStatus(obj.id, sid, status)}
            onSubtaskAccountableChange={(sid, a) => changeSubtaskAccountable(obj.id, sid, a)}
          />
        ))}
      </div>

      </div>{/* end scrollable body */}
      </div>{/* end outer flex column */}

      {/* ── Objective Modal ─────────────────────────────────────────── */}
      <Modal
        title={
          <span style={{ fontFamily: "'Albert Sans', sans-serif", fontSize: 18, fontWeight: 600 }}>
            {editingObjective ? "Edit Objective" : "New Objective"}
          </span>
        }
        open={showObjectiveModal}
        onOk={saveObjective}
        onCancel={() => setShowObjectiveModal(false)}
        confirmLoading={saving === "objective"}
        okText={editingObjective ? "Save Changes" : "Add Objective"}
        okButtonProps={{ style: { background: "var(--color-indigo-900)", border: "none" } }}
        width={520}
      >
        <Form form={objectiveForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Objective Title" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Complete CSG onboarding flow" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="What needs to happen and why it matters..." />
          </Form.Item>
          <Form.Item name="notes" label="Notes (supports markdown)">
            <TextArea rows={3} placeholder="Context, blockers, links, or anything Claude should know..." />
          </Form.Item>
          <Form.Item name="category" label="Category" initialValue="General">
            <Select
              showSearch
              allowClear
              placeholder="Select or create a category"
              options={allCategories.map((c) => ({ label: c, value: c }))}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "6px 0" }} />
                  <div style={{ display: "flex", gap: 6, padding: "4px 8px 8px" }}>
                    <Input
                      size="small"
                      placeholder="New category name"
                      value={newCategoryInput}
                      onChange={(e) => setNewCategoryInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newCategoryInput.trim()) {
                          e.stopPropagation();
                          const cat = newCategoryInput.trim();
                          setCustomCategories((prev) => prev.includes(cat) ? prev : [...prev, cat]);
                          objectiveForm.setFieldValue("category", cat);
                          setNewCategoryInput("");
                        }
                      }}
                    />
                    <Button
                      size="small"
                      type="primary"
                      icon={<PlusCircle size={12} />}
                      disabled={!newCategoryInput.trim()}
                      onClick={() => {
                        const cat = newCategoryInput.trim();
                        if (!cat) return;
                        setCustomCategories((prev) => prev.includes(cat) ? prev : [...prev, cat]);
                        objectiveForm.setFieldValue("category", cat);
                        setNewCategoryInput("");
                      }}
                      style={{ background: "var(--color-indigo-900)", border: "none", flexShrink: 0 }}
                    >
                      Add
                    </Button>
                  </div>
                </>
              )}
            />
          </Form.Item>
          <Form.Item name="accountable" label="Accountable (A)">
            <Select
              allowClear
              placeholder="Who owns this?"
              options={EXECUTIVES.map((e) => ({ label: e, value: e }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="not_started">
            <Select
              options={Object.entries(STATUS_CONFIG).map(([v, c]) => ({
                label: c.label,
                value: v,
              }))}
            />
          </Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
            <Form.Item name="task_type" label="Task Type">
              <Input placeholder="e.g. email notification, AI podcast..." />
            </Form.Item>
            <Form.Item name="timeline" label="Timeline" style={{ paddingLeft: 8 }}>
              <Input placeholder="e.g. ≥1.5 weeks, 2 days, Day 0..." />
            </Form.Item>
          </div>
          <Form.Item name="due_date" label="Due Date">
            <Input placeholder="YYYY-MM-DD (e.g. 2026-04-06)" />
          </Form.Item>
          <Form.Item name="next_action" label="Next Action">
            <TextArea rows={2} placeholder="What specifically needs to happen next..." />
          </Form.Item>
          <Form.Item name="platform_functionality_needed" label="Platform Functionality Needed">
            <TextArea rows={2} placeholder="What needs to be built inside the platform..." />
          </Form.Item>
          <Form.Item name="claude_task" label="Claude Can Drive This" valuePropName="checked">
            <Checkbox>Mark this as a Claude task</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Subtask Modal ───────────────────────────────────────────── */}
      <Modal
        title={
          <span style={{ fontFamily: "'Albert Sans', sans-serif", fontSize: 18, fontWeight: 600 }}>
            {editingSubtask ? "Edit Subtask" : "New Subtask"}
          </span>
        }
        open={showSubtaskModal}
        onOk={saveSubtask}
        onCancel={() => setShowSubtaskModal(false)}
        confirmLoading={saving === "subtask"}
        okText={editingSubtask ? "Save Changes" : "Add Subtask"}
        okButtonProps={{ style: { background: "var(--color-indigo-900)", border: "none" } }}
        width={480}
      >
        <Form form={subtaskForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Subtask" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Set up welcome email sequence" />
          </Form.Item>
          <Form.Item name="accountable" label="Accountable (A)">
            <Select
              allowClear
              placeholder="Who owns this?"
              options={EXECUTIVES.map((e) => ({ label: e, value: e }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="not_started">
            <Select
              options={Object.entries(STATUS_CONFIG).map(([v, c]) => ({
                label: c.label,
                value: v,
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Any context or blockers..." />
          </Form.Item>
        </Form>
      </Modal>
    </BaseLayout>
  );
}

// ── StatusSelect ─────────────────────────────────────────────────────────────

function StatusSelect({
  value,
  onChange,
  size = "middle",
}: {
  value: string;
  onChange: (status: string) => void;
  size?: "small" | "middle";
}) {
  const cfg = STATUS_CONFIG[value as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.not_started;
  return (
    <Select
      size={size}
      value={value}
      onChange={onChange}
      variant="borderless"
      style={{ minWidth: size === "small" ? 130 : 155 }}
      styles={{ popup: { root: { minWidth: 180 } } }}
      options={Object.entries(STATUS_CONFIG).map(([v, c]) => ({
        value: v,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: c.color, fontSize: 11, fontWeight: 600 }}>
            {c.icon} {c.label}
          </span>
        ),
      }))}
      labelRender={() => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: size === "small" ? "1px 8px" : "2px 10px",
            borderRadius: 999,
            fontSize: size === "small" ? 10 : 11,
            fontWeight: 600,
            color: cfg.color,
            background: cfg.bg,
            border: `1px solid ${cfg.color}30`,
          }}
        >
          {cfg.icon} {cfg.label}
        </span>
      )}
    />
  );
}

// ── FilterDrawers ────────────────────────────────────────────────────────────

interface FilterDrawersProps {
  uniqueCategories: string[];
  filterCategory: string | null;
  setFilterCategory: (v: string | null) => void;
  filterAccountable: string | null;
  setFilterAccountable: (v: string | null) => void;
  filterStatus: string | null;
  setFilterStatus: (v: string | null) => void;
  activeFilterCount: number;
  onClearAll: () => void;
}

function FilterDrawers({
  uniqueCategories,
  filterCategory,
  setFilterCategory,
  filterAccountable,
  setFilterAccountable,
  filterStatus,
  setFilterStatus,
  activeFilterCount,
  onClearAll,
}: FilterDrawersProps) {
  const [openPanel, setOpenPanel] = useState<"category" | "accountable" | null>(null);

  function toggle(panel: "category" | "accountable") {
    setOpenPanel((p) => (p === panel ? null : panel));
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Drawer trigger row */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {/* Category drawer trigger */}
        <button
          onClick={() => toggle("category")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 14px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            border: `1.5px solid ${filterCategory ? getCategoryColor(filterCategory) : "var(--color-border)"}`,
            background: filterCategory ? `${getCategoryColor(filterCategory)}12` : "var(--color-muted)",
            color: filterCategory ? getCategoryColor(filterCategory) : "var(--color-muted-foreground)",
            transition: "all 0.15s",
          }}
        >
          <Tag
            style={{
              margin: 0,
              padding: "0 6px",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              borderRadius: 4,
              background: filterCategory ? `${getCategoryColor(filterCategory)}22` : "transparent",
              border: "none",
              color: filterCategory ? getCategoryColor(filterCategory) : "var(--color-muted-foreground)",
            }}
          >
            TYPE
          </Tag>
          {filterCategory ?? "All categories"}
          {openPanel === "category" ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>

        {/* Accountable drawer trigger */}
        <button
          onClick={() => toggle("accountable")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 14px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            border: `1.5px solid ${filterAccountable ? EXEC_COLORS[filterAccountable] ?? "var(--color-border)" : "var(--color-border)"}`,
            background: filterAccountable ? `${EXEC_COLORS[filterAccountable] ?? "#64748b"}12` : "var(--color-muted)",
            color: filterAccountable ? EXEC_COLORS[filterAccountable] ?? "var(--color-foreground)" : "var(--color-muted-foreground)",
            transition: "all 0.15s",
          }}
        >
          <UserCheck size={13} />
          {filterAccountable
            ? filterAccountable.split(" ")[0]
            : "All owners"}
          {openPanel === "accountable" ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>

        {/* Status select — always visible */}
        <Select
          size="small"
          allowClear
          placeholder="All statuses"
          value={filterStatus ?? undefined}
          onChange={(val) => setFilterStatus(val ?? null)}
          options={Object.entries(STATUS_CONFIG).map(([v, c]) => ({ label: c.label, value: v }))}
          style={{ width: 140 }}
        />

        {/* Clear all */}
        {activeFilterCount > 0 && (
          <button
            onClick={onClearAll}
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: "var(--color-muted-foreground)",
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
          </button>
        )}
      </div>

      {/* Category panel */}
      {openPanel === "category" && (
        <div
          style={{
            marginTop: 8,
            padding: "12px 14px",
            background: "var(--color-muted)",
            borderRadius: 10,
            border: "1px solid var(--color-border)",
          }}
        >
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              onClick={() => { setFilterCategory(null); setOpenPanel(null); }}
              style={{
                padding: "3px 12px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                border: `1.5px solid ${filterCategory === null ? "var(--color-indigo-900)" : "var(--color-border)"}`,
                background: filterCategory === null ? "var(--color-indigo-900)" : "#fff",
                color: filterCategory === null ? "#fff" : "var(--color-muted-foreground)",
                transition: "all 0.15s",
              }}
            >
              All
            </button>
            {uniqueCategories.map((cat) => {
              const color = getCategoryColor(cat);
              const active = filterCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => { setFilterCategory(active ? null : cat); if (!active) setOpenPanel(null); }}
                  style={{
                    padding: "3px 12px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: `1.5px solid ${active ? color : "var(--color-border)"}`,
                    background: active ? `${color}18` : "#fff",
                    color: active ? color : "var(--color-muted-foreground)",
                    transition: "all 0.15s",
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Accountable panel */}
      {openPanel === "accountable" && (
        <div
          style={{
            marginTop: 8,
            padding: "12px 14px",
            background: "var(--color-muted)",
            borderRadius: 10,
            border: "1px solid var(--color-border)",
          }}
        >
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              onClick={() => { setFilterAccountable(null); setOpenPanel(null); }}
              style={{
                padding: "3px 12px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                border: `1.5px solid ${filterAccountable === null ? "var(--color-indigo-900)" : "var(--color-border)"}`,
                background: filterAccountable === null ? "var(--color-indigo-900)" : "#fff",
                color: filterAccountable === null ? "#fff" : "var(--color-muted-foreground)",
                transition: "all 0.15s",
              }}
            >
              All
            </button>
            {EXECUTIVES.map((exec) => {
              const color = EXEC_COLORS[exec];
              const active = filterAccountable === exec;
              const initials = exec.split(" ").map((n) => n[0]).join("");
              return (
                <button
                  key={exec}
                  onClick={() => { setFilterAccountable(active ? null : exec); if (!active) setOpenPanel(null); }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "3px 12px 3px 6px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: `1.5px solid ${active ? color : "var(--color-border)"}`,
                    background: active ? `${color}18` : "#fff",
                    color: active ? color : "var(--color-muted-foreground)",
                    transition: "all 0.15s",
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: active ? color : "#e2e8f0",
                      color: active ? "#fff" : "#64748b",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </span>
                  {exec}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ObjectiveCard ────────────────────────────────────────────────────────────

interface ObjectiveCardProps {
  objective: QBRObjective;
  saving: string | null;
  onEdit: () => void;
  onDelete: () => void;
  onAccountableChange: (a: string | null) => void;
  onStatusChange: (status: string) => void;
  onAddSubtask: () => void;
  onEditSubtask: (s: QBRSubtask) => void;
  onDeleteSubtask: (id: string) => void;
  onSubtaskStatusChange: (subtaskId: string, status: string) => void;
  onSubtaskAccountableChange: (subtaskId: string, a: string | null) => void;
}

function ObjectiveCard({
  objective,
  saving,
  onEdit,
  onDelete,
  onAccountableChange,
  onStatusChange,
  onAddSubtask,
  onEditSubtask,
  onDeleteSubtask,
  onSubtaskStatusChange,
  onSubtaskAccountableChange,
}: ObjectiveCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isSaving = saving === objective.id;

  const subtasksDone = objective.subtasks.filter((s) => s.status === FINAL_STATUS).length;
  const subtaskTotal = objective.subtasks.length;

  return (
    <div
      className="panel"
      style={{
        border: "1px solid var(--color-border)",
        background: "var(--color-card)",
        transition: "all 0.2s ease",
        opacity: isSaving ? 0.7 : 1,
      }}
    >
      {/* ── Card Header ──── */}
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        <div style={{ marginTop: 2, color: "var(--color-muted-foreground)", flexShrink: 0 }}>
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "var(--color-foreground)",
                lineHeight: 1.3,
              }}
            >
              {objective.title}
            </span>
            <Tag
              style={{
                background: "var(--color-muted)",
                border: "none",
                color: "var(--color-muted-foreground)",
                fontSize: 10,
                fontWeight: 600,
                borderRadius: 6,
                lineHeight: "18px",
              }}
            >
              {objective.category}
            </Tag>
            {objective.task_type && (
              <Tag
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-muted-foreground)",
                  fontSize: 10,
                  fontWeight: 500,
                  borderRadius: 6,
                  lineHeight: "18px",
                  fontStyle: "italic",
                }}
              >
                {objective.task_type}
              </Tag>
            )}
            {objective.due_date && objective.due_date !== "2026-04-13" && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  fontSize: 11,
                  fontWeight: 600,
                  color: dueDateColor(objective.due_date),
                }}
              >
                <Calendar size={11} />
                {formatDueDate(objective.due_date)}
              </span>
            )}
            {objective.claude_task && (
              <Tooltip title="Claude can drive this task">
                <span style={{ fontSize: 13, lineHeight: 1, cursor: "default" }}>🤖</span>
              </Tooltip>
            )}
          </div>

          {objective.description && (
            <MarkdownText
              text={objective.description}
              style={{
                margin: "0 0 8px",
                fontSize: 13,
                color: "var(--color-muted-foreground)",
                lineHeight: 1.5,
              }}
            />
          )}

          <div
            style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Inline status selector */}
            <StatusSelect
              value={objective.status}
              onChange={onStatusChange}
            />

            {/* Inline accountable selector */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <UserCheck size={12} style={{ color: "var(--color-muted-foreground)", flexShrink: 0 }} />
              <Select
                size="small"
                allowClear
                placeholder="Assign A"
                value={objective.accountable ?? undefined}
                onChange={(val) => onAccountableChange(val ?? null)}
                options={EXECUTIVES.map((e) => ({ label: e, value: e }))}
                style={{ width: 130, fontSize: 12 }}
                variant="borderless"
                styles={{
                  popup: { root: { minWidth: 160 } }
                }}
              />
            </div>

            {subtaskTotal > 0 && (
              <span style={{ fontSize: 12, color: "var(--color-muted-foreground)" }}>
                {subtasksDone}/{subtaskTotal} subtasks
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: "flex", gap: 4 }}>
            <Tooltip title="Edit">
              <button
                className="icon-button"
                style={{ padding: 4, borderRadius: 6, cursor: "pointer", color: "var(--color-muted-foreground)" }}
                onClick={onEdit}
              >
                <PencilLine size={15} />
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button
                className="icon-button"
                style={{ padding: 4, borderRadius: 6, cursor: "pointer", color: "#f87171" }}
                onClick={onDelete}
              >
                <Trash2 size={15} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* ── Subtasks ──── */}
      {expanded && (
        <div
          style={{
            borderTop: "1px solid var(--color-border)",
            padding: "12px 20px 16px 46px",
          }}
        >
          {/* Next Action — most actionable field, shown first */}
          {objective.next_action && (
            <div
              style={{
                marginBottom: 10,
                padding: "8px 12px",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>
                Next Action
              </div>
              <div style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.5 }}>{objective.next_action}</div>
            </div>
          )}

          {/* Platform Functionality Needed */}
          {objective.platform_functionality_needed && (
            <div
              style={{
                marginBottom: 10,
                padding: "8px 12px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>
                Platform Needed
              </div>
              <div style={{ fontSize: 13, color: "#15803d", lineHeight: 1.5 }}>{objective.platform_functionality_needed}</div>
            </div>
          )}

          {objective.notes && (
            <MarkdownText
              text={objective.notes}
              style={{
                fontSize: 13,
                color: "var(--color-muted-foreground)",
                lineHeight: 1.6,
                marginBottom: 12,
                padding: "10px 14px",
                background: "var(--color-muted)",
                borderRadius: 8,
                borderLeft: "3px solid var(--color-primary)",
              }}
            />
          )}
          {objective.subtasks.length === 0 && (
            <p style={{ fontSize: 12, color: "var(--color-muted-foreground)", margin: "4px 0 12px" }}>
              No subtasks yet.
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {objective.subtasks.map((sub) => (
              <SubtaskRow
                key={sub.id}
                subtask={sub}
                onEdit={() => onEditSubtask(sub)}
                onDelete={() => onDeleteSubtask(sub.id)}
                onStatusChange={(status) => onSubtaskStatusChange(sub.id, status)}
                onAccountableChange={(a) => onSubtaskAccountableChange(sub.id, a)}
              />
            ))}
          </div>

          <Button
            size="small"
            icon={<PlusCircle size={13} />}
            onClick={onAddSubtask}
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              color: "var(--color-indigo-800)",
              border: "1px dashed var(--color-indigo-800)",
              borderRadius: 6,
            }}
          >
            Add Subtask
          </Button>
        </div>
      )}
    </div>
  );
}

// ── SubtaskRow ───────────────────────────────────────────────────────────────

interface SubtaskRowProps {
  subtask: QBRSubtask;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: string) => void;
  onAccountableChange: (a: string | null) => void;
}

function SubtaskRow({ subtask, onEdit, onDelete, onStatusChange, onAccountableChange }: SubtaskRowProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "8px 12px",
        borderRadius: 8,
        background: "var(--color-muted)",
        border: "1px solid transparent",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: subtask.notes ? 4 : 0 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--color-foreground)",
              textDecoration: subtask.status === FINAL_STATUS ? "line-through" : "none",
              opacity: subtask.status === FINAL_STATUS ? 0.6 : 1,
            }}
          >
            {subtask.title}
          </span>

          <StatusSelect value={subtask.status} onChange={onStatusChange} size="small" />

          {/* Inline accountable */}
          <Select
            size="small"
            allowClear
            placeholder="Assign A"
            value={subtask.accountable ?? undefined}
            onChange={(val) => onAccountableChange(val ?? null)}
            options={EXECUTIVES.map((e) => ({ label: e, value: e }))}
            style={{ width: 120, fontSize: 11 }}
            variant="borderless"
            styles={{ popup: { root: { minWidth: 160 } } }}
          />
        </div>
        {subtask.notes && (
          <MarkdownText
            text={subtask.notes}
            style={{ fontSize: 11, color: "var(--color-muted-foreground)", margin: 0 }}
          />
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <Tooltip title="Edit">
          <button
            className="icon-button"
            style={{ padding: 3, cursor: "pointer", color: "var(--color-muted-foreground)", borderRadius: 4 }}
            onClick={onEdit}
          >
            <PencilLine size={13} />
          </button>
        </Tooltip>
        <Tooltip title="Delete">
          <button
            className="icon-button"
            style={{ padding: 3, cursor: "pointer", color: "#f87171", borderRadius: 4 }}
            onClick={onDelete}
          >
            <Trash2 size={13} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
